import Fuse from "fuse.js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import removeAccents from "remove-accents";
import {
  Check,
  Import,
  NoShowPassword,
  ShowPassword,
  WarningCircle,
} from "../../assets/icons/index";
import SelectMultipleTrigger from "../selectTrigger/SelectMultipleTrigger";

const StyledInput = ({
  label,
  type,
  unit,
  list,
  fileName,
  fileAccept,
  fileSize,
  placeholder,
  value,
  checked,
  onChange,
  onBlur,
  error,
  imgFront,
  isSearchable,
  isSecondary,
  disabled,
  footer,
  renderTriggerValue,
}) => {
  const [typeInputChange, setTypeInputChange] = useState(type || "text");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredList, setFilteredList] = useState(list);
  const selectMultipleRef = useRef();
  const multiSelectTriggerRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [dropdownPanelRect, setDropdownPanelRect] = useState(null);

  const isMultiSelectOpen =
    isSelectOpen &&
    (type === "selectMultipleCheckbox" || type === "selectMultipleRadio");

  useLayoutEffect(() => {
    if (!isMultiSelectOpen) {
      setDropdownPanelRect(null);
      return;
    }

    const triggerEl = multiSelectTriggerRef.current;
    if (!triggerEl) return;

    const updateRect = () => {
      const rect = triggerEl.getBoundingClientRect();
      const gap = 8;
      const margin = 12;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const width = Math.max(rect.width, 224);
      let left = rect.left;
      if (left + width > vw - margin) left = vw - margin - width;
      if (left < margin) left = margin;

      const top = rect.bottom + gap;
      const maxHeight = Math.min(300, Math.max(120, vh - top - margin));

      setDropdownPanelRect({ top, left, width, maxHeight });
    };

    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isMultiSelectOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (
        selectMultipleRef.current?.contains(e.target) ||
        dropdownPanelRef.current?.contains(e.target)
      ) {
        return;
      }
      setIsSelectOpen(false);
    };
    document.addEventListener("click", handler);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (isSearchable && search && list.length > 0) {
      const searchTerms = search
        .split(" ")
        .map((term) => removeAccents(term.toLowerCase()));

      const fuseKeys =
        list.some(
          (item) =>
            item.searchText != null && String(item.searchText).trim() !== "",
        )
          ? ["label", "searchText"]
          : ["label"];

      const options = {
        keys: fuseKeys,
        includeScore: true,
        threshold: 0.3,
        distance: 100,
        shouldSort: true,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null) {
            return "";
          }
          return removeAccents(value.toString().toLowerCase());
        },
      };

      const fuse = new Fuse(list, options);
      const combinedResults = {};

      searchTerms.forEach((term) => {
        const result = fuse.search(term);
        result.forEach(({ item, score }) => {
          const rowKey = String(item.value ?? item.id ?? item.label ?? "");
          if (combinedResults[rowKey]) {
            combinedResults[rowKey].score -= score;
          } else {
            combinedResults[rowKey] = { item, score };
          }
        });
      });

      const sortedResults = Object.values(combinedResults).sort(
        (a, b) => a.score - b.score
      );
      setFilteredList(sortedResults.map((result) => result.item));
    } else {
      setFilteredList(list);
    }
  }, [search, list, isSearchable]);

  const deleteAllChecked = () => {
    onChange(list.map((item) => ({ ...item, checked: false })));
  };

  return (
    <>
      {(type === "text" ||
        type === "password" ||
        type === "number" ||
        type === "time") && (
        <label className={"flex flex-col gap-1"}>
          {label && <div className={"text-sm text-gray-500"}>{label}</div>}
          <div className={"relative"}>
            {imgFront ? (
              <div
                className={
                  "absolute left-0 top-1/2 transform -translate-y-1/2 p-1.5"
                }
              >
                {imgFront}
              </div>
            ) : null}
            <input
              type={typeInputChange}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              className={`w-full border rounded-md p-1.5 ${type === "password" ? "pr-7" : ""} ${type === "number" ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" : ""} ${unit ? "pr-20" : ""} ${imgFront ? "pl-7" : ""} ${disabled ? "bg-gray-50 text-gray-600 cursor-not-allowed" : ""}`}
              {...(type === "number" && {
                pattern: "[0-9]*",
                inputMode: "numeric",
              })}
              {...(type === "email" && { inputMode: "email" })}
            />
            {type === "password" && (
              <>
                <div
                  className="absolute inset-y-0 right-0 flex items-center px-2 cursor-pointer"
                  onClick={() => {
                    if (typeInputChange === "password")
                      setTypeInputChange("text");
                    if (typeInputChange === "text")
                      setTypeInputChange("password");
                  }}
                >
                  {typeInputChange === "text" ? (
                    <NoShowPassword wh={18} color={"#666666"} strokeW={2} />
                  ) : (
                    <ShowPassword wh={18} color={"#666666"} strokeW={2} />
                  )}
                </div>
              </>
            )}
            {unit && (
              <div
                className={"absolute inset-y-0 right-0 flex items-center pr-3"}
              >
                {unit}
              </div>
            )}
          </div>
          {error && (
            <div className={"text-red-500 flex items-center gap-1 text-xs"}>
              <WarningCircle wh={16} color={"#EF4444"} />
              {error}
            </div>
          )}
        </label>
      )}
      {(type === "checkbox" || type === "checkbox-round") && (
        <label
          className={
            "flex gap-2 items-start relative cursor-pointer justify-between w-full"
          }
        >
          <div className={"flex gap-4 items-start relative min-w-0 flex-1"}>
            <input
              type={"checkbox"}
              placeholder={placeholder}
              checked={checked}
              onChange={onChange}
              className={`${type === "checkbox-round" ? "w-5 h-5 border rounded appearance-none checked:bg-workfully-700 checked:border-transparent" : "w-4 h-4 accent-workfully-700"} ${isSecondary ? "hidden" : ""}`}
            />
            {type === "checkbox-round" && (
              <div
                className={"absolute inset-0 flex items-center justify-center"}
              >
                <Check wh={14} color={"#FFFFFF"} />
              </div>
            )}
            {label &&
              (typeof label === "string" ? (
                <div className={""}>{label}</div>
              ) : (
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                  {label}
                </div>
              ))}
          </div>
          <div className={""}>
            {checked && isSecondary && <Check wh={16} color={"#374151"} />}
          </div>
        </label>
      )}
      {type === "radio" && (
        <label
          className={
            "flex gap-2 items-start relative cursor-pointer justify-between w-full"
          }
        >
          <div className={"flex gap-2 items-start relative min-w-0 flex-1"}>
            <input
              type={"radio"}
              placeholder={placeholder}
              checked={checked}
              onChange={onChange}
              className={`mt-0.5 w-4 h-4 shrink-0 accent-workfully-700 ${isSecondary ? "hidden" : ""}`}
            />
            {label &&
              (typeof label === "string" ? (
                <div className={""}>{label}</div>
              ) : (
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                  {label}
                </div>
              ))}
          </div>
          <div className={""}>
            {checked && isSecondary && <Check wh={16} color={"#374151"} />}
          </div>
        </label>
      )}
      {type === "file" && (
        <label className={"flex flex-col gap-1"}>
          <div className={"text-gray-500"}>{label}</div>
          <label className="w-full flex flex-row  border border-gray-300 rounded-lg h-11 shadow-sm">
            <div className="ft-sm text-gray-700 p-4 flex items-center justify-center gap-2 border-r border-gray-300">
              <Import color={"#374151"} wh={14} />
              Importer
            </div>
            <div className="ft-sm truncate overflow-hidden text-gray-400 flex items-center ml-3">
              {fileName ? fileName : "Aucun fichier"}
            </div>
            <input
              type={type}
              onChange={onChange}
              accept={fileAccept.map((type) => type.name).join(", ")}
              className="hidden"
            />
          </label>
          <div className="ft-xs text-gray-400">
            {fileAccept.length > 1
              ? "Formats acceptés : "
              : "Format accepté : "}
            {fileAccept.map((type) => type.title).join(", ")} ({fileSize} max)
          </div>
          {error && (
            <div className={"text-red-500 flex items-center gap-1 text-xs"}>
              <WarningCircle wh={16} color={"#EF4444"} />
              {error}
            </div>
          )}
        </label>
      )}
      {type === "select" && (
        <label className={"flex flex-col gap-1"}>
          {label && <div className={"text-sm text-gray-500"}>{label}</div>}
          <div className={"relative"}>
            {imgFront ? (
              <div
                className={
                  "absolute left-0 top-1/2 transform -translate-y-1/2 p-1.5"
                }
              >
                {imgFront}
              </div>
            ) : null}
            <select
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              className={`w-full border rounded-md px-3 py-2 ${imgFront ? "pl-7" : ""}`}
            >
              {filteredList.map((item, index) => (
                <option key={index} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {error && (
              <div className={"text-red-500 flex items-center gap-1 text-xs"}>
                <WarningCircle wh={16} color={"#EF4444"} />
                {error}
              </div>
            )}
          </div>
        </label>
      )}
      {type === "selectMultipleCheckbox" && (
        <div
          className={"flex flex-col gap-1 relative w-full cursor-pointer"}
          ref={selectMultipleRef}
        >
          {label && <div className={"text-sm text-gray-500"}>{label}</div>}
          <div ref={multiSelectTriggerRef} className="w-full">
            <SelectMultipleTrigger
              hasSelection={Boolean(
                filteredList && filteredList.some((item) => item.checked),
              )}
              value={value}
              renderValue={renderTriggerValue}
              onToggleOpen={() => setIsSelectOpen(!isSelectOpen)}
              variant="checkbox"
              onClearAll={deleteAllChecked}
            />
          </div>
          {isSelectOpen &&
            dropdownPanelRect &&
            type === "selectMultipleCheckbox" &&
            createPortal(
              <div
                ref={dropdownPanelRef}
                className={
                  "overflow-y-auto rounded border bg-white py-4 px-3 shadow-2xl"
                }
                style={{
                  position: "fixed",
                  top: dropdownPanelRect.top,
                  left: dropdownPanelRect.left,
                  width: dropdownPanelRect.width,
                  maxHeight: dropdownPanelRect.maxHeight,
                  zIndex: 10000,
                }}
              >
                <div className={"flex flex-col gap-4"}>
                  {isSearchable && (
                    <div className={""}>
                      <StyledInput
                        type={"text"}
                        placeholder={"Rechercher..."}
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                        }}
                      />
                    </div>
                  )}
                  <div className={"flex flex-col gap-1 ft-sm"}>
                    {filteredList.map((item, index) => (
                      <div
                        key={String(item.value ?? item.id ?? index)}
                        className={
                          "ft-sm rounded-md px-2 py-1.5 -mx-1 transition-colors hover:bg-gray-100"
                        }
                      >
                        <div className={"flex gap-1 items-start"}>
                          <StyledInput
                            label={item.labelNode ?? item.label}
                            type={"checkbox"}
                            isSecondary
                            checked={item.checked}
                            onChange={(e) => {
                              const toggledValue = String(item.value ?? "");
                              onChange(
                                list.map((i) =>
                                  String(i.value ?? "") === toggledValue
                                    ? { ...i, checked: e.target.checked }
                                    : i,
                                ),
                              );
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>,
              document.body,
            )}
          {error && (
            <div className={"text-red-500 flex items-center gap-1 text-xs"}>
              <WarningCircle wh={16} color={"#EF4444"} />
              {error}
            </div>
          )}
        </div>
      )}
      {type === "selectMultipleRadio" && (
        <div
          className={"flex flex-col gap-1 relative w-full cursor-pointer"}
          ref={selectMultipleRef}
        >
          {label && <div className={"text-sm text-gray-500"}>{label}</div>}
          <div ref={multiSelectTriggerRef} className="w-full">
            <SelectMultipleTrigger
              hasSelection={Boolean(
                filteredList && filteredList.some((item) => item.checked),
              )}
              value={value}
              renderValue={renderTriggerValue}
              onToggleOpen={() => setIsSelectOpen(!isSelectOpen)}
              variant="radio"
            />
          </div>
          {isSelectOpen &&
            dropdownPanelRect &&
            type === "selectMultipleRadio" &&
            createPortal(
              <div
                ref={dropdownPanelRef}
                className={
                  "flex min-h-0 flex-col rounded border bg-white shadow-2xl"
                }
                style={{
                  position: "fixed",
                  top: dropdownPanelRect.top,
                  left: dropdownPanelRect.left,
                  width: dropdownPanelRect.width,
                  maxHeight: dropdownPanelRect.maxHeight,
                  zIndex: 10000,
                }}
              >
                {isSearchable && (
                  <div className={"border-b border-gray-100 px-3 pb-2 pt-3"}>
                    <StyledInput
                      type={"text"}
                      placeholder={"Rechercher..."}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                )}
                <div className={"min-h-0 flex-1 overflow-y-auto px-3 py-2"}>
                  <div className={"flex flex-col gap-1 ft-sm"}>
                    {filteredList.map((item, index) => (
                      <div
                        key={index}
                        className={
                          "ft-sm rounded-md px-2 py-1.5 -mx-1 transition-colors hover:bg-gray-100"
                        }
                      >
                        <div className={"flex gap-1 items-start"}>
                          <StyledInput
                            label={item.labelNode ?? item.label}
                            type={"radio"}
                            isSecondary
                            checked={item.checked}
                            onChange={(e) => {
                              onChange(
                                list.map((i) =>
                                  i.value === item.value
                                    ? { ...i, checked: e.target.checked }
                                    : { ...i, checked: false },
                                ),
                              );
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {footer && (
                  <div className="sticky bottom-0 rounded-b border-t border-gray-100 bg-white px-3 py-2">
                    {footer}
                  </div>
                )}
              </div>,
              document.body,
            )}
          {error && (
            <div className={"text-red-500 flex items-center gap-1 text-xs"}>
              <WarningCircle wh={16} color={"#EF4444"} />
              {error}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StyledInput;
